import {
  ProviderOrganizationPreference,
  ProviderProfile,
  ProviderNoteSection,
  User,
  UserPreferences,
} from '.';
import { BaseItem, Gender } from './common';
import { Kind } from './ehrProviderInformation';
import { Specialty } from './encounter';
import { Practice } from './practice';
import { LanguageWithOther } from './previsit/language';
import { EducationKind } from './providerProfile';

export type ProviderWithEnabledPractices = Provider & {
  enabledPractices?: Practice[] | null;
};

export interface Provider extends User.User {
  profile?: ProviderProfile.Profile;
  organizationPreferences?: OrganizationPreference[];
}

export type OrganizationPreference = {
  organizationId: string;
  preference: ProviderOrganizationPreference.Preference;
};

export type FindAllOptions = Partial<{
  q: string;
  ids: string[];
  practiceId: string;
  practiceGroupId: string;
}>;

interface UpdateValues
  extends Partial<Omit<Provider, 'id' | 'created' | 'updated'>> {}

export type UpsertValues = UpdateValues &
  Pick<
    Provider,
    'firstName' | 'lastName' | 'email' | 'profile' | 'organizationPreferences'
  >;

export interface EhrProviderInformation extends BaseItem {
  practiceId: string;
  providerUserId: string;
  value: string;
  specialties: Specialty[] | null;
  phoneNumber: string | null;
  kind: Kind;
}

export const PROVIDER_IMPORT_COLUMNS: (keyof CsvBulkUpdateValues)[] = [
  'firstName',
  'lastName',
  'prefix',
  'suffix',
  'email',
  'dateOfBirth',
  'practiceId',
  'providerEhrId',
  'providerApiId',
  'role',
  'registered',
  'phoneNumber',
  'phoneNumberVerified',
  'sex',
  'npi',
  'kind',
  'internal',
  'imageName', // This is used to match uploaded pictures to provider row
  'virtualVisitUrl',
  'deleted',
  'autoApplyPrevisitFormTemplates',
  'noteSectionHeader',
  'noteSectionHpi',
  'noteSectionRos',
];

interface CsvImportNoteSection {
  noteSectionHeader: ProviderNoteSection.UpdateValues['displayName'];
  noteSectionHpi: ProviderNoteSection.UpdateValues['displayName'];
  noteSectionRos: ProviderNoteSection.UpdateValues['displayName'];
}

export interface CsvBulkUpdateValues
  extends Omit<User.UpdateValues, 'dateOfBirth'>,
    Pick<
      Partial<UserPreferences.FullPreferenceDetail>,
      'autoApplyPrevisitFormTemplates'
    >,
    CsvImportNoteSection {
  dateOfBirth?: string | null; // should be ISO
  providerEhrId: string;
  providerApiId?: string | null;
  practiceId: string;
  imageName?: string;
}

export interface ImportValidation {
  value: string;
  error?: ImportError;
}

export enum ImportError {
  InvalidFormat = 'INVALID_FORMAT',
  NotFound = 'NOT_FOUND',
  NotImplemented = 'NOT_IMPLEMENTED',
  NotMatched = 'NOT_MATCHED',
  Unused = 'UNUSED',
  WillBeCreated = 'WILL_BE_CREATED',
  Missing = 'MISSING',
}

export const ImportErrorMapping: Record<ImportError, string> = {
  [ImportError.InvalidFormat]: 'Invalid format',
  [ImportError.NotFound]: 'Not found',
  [ImportError.NotImplemented]: 'Not implemented',
  [ImportError.NotMatched]: 'Not matched',
  [ImportError.Unused]: 'Unused',
  [ImportError.WillBeCreated]: 'Will be created',
  [ImportError.Missing]: 'Missing',
};

export const IMPORT_ERRORS = [
  ImportError.InvalidFormat,
  ImportError.NotFound,
  ImportError.NotImplemented,
];

export const IMPORT_WARNINGS = [
  ImportError.NotMatched,
  ImportError.WillBeCreated,
  ImportError.Unused,
];

export interface ProviderCsv
  extends Pick<
    User.UpdateValues,
    | 'firstName'
    | 'lastName'
    | 'email'
    | 'registered'
    | 'phoneNumber'
    | 'phoneNumberVerified'
    | 'firstName'
    | 'lastName'
    | 'prefix'
    | 'suffix'
    | 'sex'
    | 'kind'
    | 'virtualVisitUrl'
  > {
  npi: string;
  practiceId: string;
  imageUrl?: string;
  // Profile
  about?: string;
  carePhilosophy?: string;
  areasOfInterest?: string;
  gender?: Gender;
  pronouns?: string;
  spokenLanguages?: LanguageWithOther;
  specialties?: Specialty;
  educationInstitution?: string;
  educationKind?: EducationKind;
  educationYearEnded?: string;
  positionName?: string;
  positionOrganization?: string;
  positionLocation?: string;
  positionYears?: string;
  certification?: string;
  certificationInstitution?: string;
  certificationYear?: string;
  professionalMembershipName?: string;
  publication?: string;
  publicationPublisher?: string;
  publicationYear?: string;
  award?: string;
  awardSponsor?: string;
  awardYear?: string;
  // ProviderOrganizationPreference
  displayInDirectory?: boolean;
  acceptsNewPatients?: boolean;
  offersOnlineScheduling?: boolean;
  offersVirtualVisits?: boolean;
  schedulingLink?: string;
  videoUrl?: string;
  reputationId?: string;
  acceptedPayorWithPlanTypes?: string;
  slug?: string;
  providerDirectoryUrl?: string;
  ableToSchedule?: boolean;
}

export const PROVIDER_CSV_IMPORT_COLUMNS: (keyof ProviderCsv)[] = [
  'firstName',
  'lastName',
  'npi',
  'email',
  'practiceId',
  'phoneNumber',
  'phoneNumberVerified',
  'registered',
  'prefix',
  'suffix',
  'sex',
  'virtualVisitUrl',
  'imageUrl',
  // Profile
  'about',
  'carePhilosophy',
  'areasOfInterest',
  'gender',
  'pronouns',
  'spokenLanguages',
  'specialties',
  'educationInstitution',
  'educationKind',
  'educationYearEnded',
  'positionName',
  'positionOrganization',
  'positionLocation',
  'positionYears',
  'certification',
  'certificationInstitution',
  'certificationYear',
  'professionalMembershipName',
  'publication',
  'publicationPublisher',
  'publicationYear',
  'award',
  'awardSponsor',
  'awardYear',
  // ProviderOrganizationPreference
  'displayInDirectory',
  'acceptsNewPatients',
  'offersOnlineScheduling',
  'offersVirtualVisits',
  'schedulingLink',
  'videoUrl',
  'reputationId',
  'acceptedPayorWithPlanTypes',
  'slug',
  'providerDirectoryUrl',
  'ableToSchedule',
];
